import { startWixConnect } from '../utils/wixConnect';
import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faCircleInfo, faCircleCheck, faCircleXmark, faPlug } from "@fortawesome/free-solid-svg-icons";
import ManageConnectionsModal from "./ManageConnectionsModal";
import ConnectParamsModal from "./ConnectParamsModal";
import { guardWrite } from "../utils/permissions";
import "./DataConnect.css";

const API_URL = process.env.REACT_APP_API_BASE_URL;

// Per-app logo from /public. Falls back to a generic icon for apps without one yet.
const PROVIDER_LOGO = { calendly: "/calendly-logo.png", calcom: "/calcom-logo.png", hubspot: "/hubspot-logo.png", stripe: "/stripe-logo.png", mailchimp: "/mailchimp-logo.png", zendesk: "/zendesk-logo.png", wix: "/wix-logo.png" };

// Embedded (Wix iframe) -> OAuth popup; standalone (e.g. local) -> same-tab
// redirect (a popup opened after an await is blocked outside an iframe). Mirrors DataConnect.
const isInIframe = () => {
  try { return window.self !== window.top; } catch { return true; }
};

/* Apps (third-party integrations). Same layout + flow as Data Sources / Messaging:
   one card per app, Connect, and Manage connections (reusing ManageConnectionsModal,
   connectionType="data" — these connections live in data_connections like Drive/OneDrive).
   Connecting an app only makes it AVAILABLE; a bot uses it only when a skill includes it. */
function Integrations({ variant = 'page', activeCategories = [], onlyProvider = null, seedProvider = null, botId = null }) {
  const { t } = useTranslation();
  // `seedProvider` (passed by the Apps modal) renders the detail panel instantly
  // from the already-fetched provider, then we refresh in the background.
  const [providers, setProviders] = useState(seedProvider ? [seedProvider] : []);
  const [loading, setLoading] = useState(!seedProvider);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activePlatform, setActivePlatform] = useState(null);
  const [connectModalProvider, setConnectModalProvider] = useState(null);
  const oauthPopupRef = useRef(null);

  const fetchProviders = useCallback(async () => {
    try {
      // Wix connected status is per-agent; scope to botId when provided. OAuth apps
      // are org-level and unaffected by bot_id.
      const url = `${API_URL}integrations${botId ? `?bot_id=${encodeURIComponent(botId)}` : ""}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (!res.ok) throw new Error("failed");
      const data = await res.json();
      setProviders(Array.isArray(data.providers) ? data.providers : []);
    } catch {
      setProviders([]);
    } finally {
      setLoading(false);
    }
  }, [botId]);

  useEffect(() => { fetchProviders(); }, [fetchProviders]);

  // Popup side: the OAuth callback redirected the popup here -> tell the opener and close.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("integration") && params.get("status") && window.opener) {
      try { window.opener.postMessage({ type: "integration-oauth" }, window.location.origin); } catch { /* opener gone */ }
      setTimeout(() => window.close(), 400);
    }
  }, []);

  // Opener side: refresh when a popup reports completion.
  useEffect(() => {
    const onMsg = (e) => {
      if (e.origin !== window.location.origin) return;
      if (e.data?.type === "integration-oauth") {
        fetchProviders();
        if (oauthPopupRef.current && !oauthPopupRef.current.closed) oauthPopupRef.current.close();
        oauthPopupRef.current = null;
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [fetchProviders]);

  const handleConnect = (key) => {
    if (!guardWrite()) return;
    // Implicit apps (e.g. Wix) connect via the platform install, not an OAuth flow —
    // there is nothing to start here.
    const target = providers.find((p) => p.key === key);
    if (target && target.implicit) return;
    // Providers that need a pre-OAuth input (e.g. Zendesk's subdomain) collect it in a
    // styled modal first; everything else connects straight away.
    const provider = providers.find((p) => p.key === key);
    if (provider && (provider.connect_params || []).length > 0) {
      setConnectModalProvider(provider);
      return;
    }
    doConnect(key, {});
  };

  const doConnect = async (key, paramValues) => {
    const qs = new URLSearchParams(paramValues || {});
    try {
      const suffix = qs.toString() ? `?${qs.toString()}` : "";
      const res = await fetch(`${API_URL}integrations/${key}/connect${suffix}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (!res.ok) { alert(t("integrations_connect_init_failed")); return; }
      const data = await res.json();
      if (!data.authorization_url) { alert(t("integrations_connect_init_failed")); return; }
      if (isInIframe()) {
        const width = 600, height = 700;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;
        oauthPopupRef.current = window.open(
          data.authorization_url, "integration-oauth",
          `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`,
        );
        const checkPopup = setInterval(() => {
          if (oauthPopupRef.current?.closed) { clearInterval(checkPopup); oauthPopupRef.current = null; fetchProviders(); }
        }, 1000);
      } else {
        window.location.href = data.authorization_url;
      }
    } catch (e) {
      console.error("Failed to connect integration:", e);
      alert(t("integrations_connect_failed"));
    }
  };

  const handleManageConnections = (key) => { setActivePlatform(key); setIsModalOpen(true); };
  const handleModalClose = () => { setIsModalOpen(false); setActivePlatform(null); };

  // Wix is not OAuth-connected: "Connect" sends the owner to the Wix App Market
  // installer — the SAME flow as the Channels page. Needs a bot to attach the site to.
  const handleWixConnect = () => {
    if (!guardWrite()) return;
    if (!botId) { alert(t("integrations_connect_init_failed")); return; }
    // Legacy connect, same as Channels (live_chat_new): plaintext state botId:userId,
    // installer with redirectUrl -> WixConnect -> connect_bot.
    startWixConnect(botId);
  };

  const activeProvider = providers.find((p) => p.key === activePlatform);
  const activeConnections = activeProvider?.connected
    ? [{
        id: activeProvider.connected.id,
        platform: activeProvider.key,
        display_name: activeProvider.connected.display_name,
        status: activeProvider.connected.status,
        external_id: "",
      }]
    : [];

  // In the unified Connect page (variant='cards') show only providers whose category
  // is active; on the standalone page show all. Cards reuse the same markup either way.
  // Fail-open: an app with no/unknown category still shows (e.g. before the backend
  // ships `category`, or a not-yet-categorized app) so apps are never silently hidden.
  const shownProviders = variant === 'cards'
    ? providers.filter((p) => !p.category || (activeCategories || []).includes(p.category))
    : providers;

  const cards = shownProviders.map((p) => {
    const count = p.connected ? 1 : 0;
    return (
      <div className="data-platform-card" key={p.key}>
        {count > 0 && (
          <div className="connected-success">
            <FontAwesomeIcon icon={faCircleCheck} style={{ color: "#5616ea", marginRight: "8px" }} />
            {t("integrations_account_connected", { count })}
          </div>
        )}

        <div className="platform-card-content">
          <div className="platform-icon">
            {PROVIDER_LOGO[p.key]
              ? <img src={PROVIDER_LOGO[p.key]} alt={p.label} style={{ height: "64px", width: "auto", borderRadius: "4px" }} />
              : <FontAwesomeIcon icon={faPlug} style={{ fontSize: "48px", color: "#5616ea" }} />}
          </div>
          <h3>{p.label}</h3>
          <p>{p.summary}</p>

          {loading ? (
            <div style={{ padding: "12px" }}>
              <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: "1.5rem", color: "#5616ea" }} />
            </div>
          ) : p.implicit ? (
            (count === 0 && p.key === "wix") ? (
              <div className="button-group">
                <Button onClick={handleWixConnect} variant="contained" disabled={!botId}
                  sx={{ backgroundColor: "#5616ea", "&:hover": { backgroundColor: "#4a14d6" } }} fullWidth>
                  {t("integrations_connect_provider", { provider: p.label })}
                </Button>
              </div>
            ) : (
              <div className="data-platform-implicit">
                <FontAwesomeIcon icon={faCircleCheck} style={{ color: "#5616ea", marginRight: "8px" }} />
                {t("integrations_implicit_note")}
              </div>
            )
          ) : (
            <div className="button-group">
              <Button
                onClick={() => handleConnect(p.key)}
                variant="contained"
                sx={{ backgroundColor: "#5616ea", "&:hover": { backgroundColor: "#4a14d6" } }}
                fullWidth
              >
                {count > 0 ? t("integrations_add_another_account") : t("integrations_connect_provider", { provider: p.label })}
              </Button>

              {count === 0 ? (
                <span className="disabled-button-wrapper">
                  <Button
                    variant="outlined"
                    disabled
                    sx={{ color: "#5616ea", borderColor: "#5616ea", "&.Mui-disabled": { color: "#9e9e9e", borderColor: "#e0e0e0", backgroundColor: "#f8f8f8" } }}
                    fullWidth
                  >
                    {t("integrations_manage_connections_count", { count: 0 })}
                  </Button>
                </span>
              ) : (
                <Button
                  onClick={() => handleManageConnections(p.key)}
                  variant="outlined"
                  sx={{ color: "#5616ea", borderColor: "#5616ea", "&:hover": { borderColor: "#5e0fb3", backgroundColor: "#f3eaff" } }}
                  fullWidth
                >
                  {t("integrations_manage_connections_count", { count })}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  });

  const modals = (
    <>
      <ManageConnectionsModal
        open={isModalOpen}
        onClose={handleModalClose}
        connections={activeConnections}
        onUpdate={fetchProviders}
        platform={activePlatform}
        connectionType="data"
        botId={botId}
      />

      <ConnectParamsModal
        open={!!connectModalProvider}
        provider={connectModalProvider}
        onClose={() => setConnectModalProvider(null)}
        onSubmit={(values) => {
          const key = connectModalProvider?.key;
          setConnectModalProvider(null);
          if (key) doConnect(key, values);
        }}
      />
    </>
  );

  // Single-app connect/manage panel, rendered inside the Apps page modal. The
  // app logo + name already live in the modal head, so this shows just the
  // description, capabilities and the connect/manage controls — reusing the
  // same OAuth / params / manage-connections logic as the cards.
  if (variant === 'detail') {
    const p = providers.find((x) => x.key === onlyProvider);
    if (!p) {
      return (
        <div className="app-detail app-detail-loading">
          <FontAwesomeIcon icon={faSpinner} spin style={{ color: "#5616ea" }} />
        </div>
      );
    }
    const count = p.connected ? 1 : 0;
    return (
      <div className="app-detail">
        {p.summary && <p className="app-detail-summary">{p.summary}</p>}

        {((p.can || []).length > 0 || (p.cannot || []).length > 0) && (
          <div className="app-detail-caps">
            {(p.can || []).length > 0 && (
              <div className="app-cap">
                <div className="app-cap-head app-cap-can">
                  <FontAwesomeIcon icon={faCircleCheck} /> {t("integrations_can_do_heading")}
                </div>
                <ul>
                  {p.can.map((c, i) => <li key={i}>{c}</li>)}
                </ul>
              </div>
            )}
            {(p.cannot || []).length > 0 && (
              <div className="app-cap">
                <div className="app-cap-head app-cap-cannot">
                  <FontAwesomeIcon icon={faCircleXmark} /> {t("integrations_cannot_do_heading")}
                </div>
                <ul>
                  {p.cannot.map((c, i) => <li key={i}>{c}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}

        {count > 0 && (
          <div className="app-detail-connected">
            <FontAwesomeIcon icon={faCircleCheck} />
            {t("studio_hero_apps_connected")}{p.connected.display_name ? ` · ${p.connected.display_name}` : ""}
          </div>
        )}

        {p.implicit ? (
          (count === 0 && p.key === "wix") ? (
            <div className="app-detail-buttons">
              <button type="button" className="app-btn app-btn-primary" onClick={handleWixConnect} disabled={!botId}>
                {t("integrations_connect_provider", { provider: p.label })}
              </button>
            </div>
          ) : p.key === "wix" ? (
            // Connected Wix: same "Manage connection" button + modal as every other
            // app (HubSpot/Stripe/...). The modal's delete routes to wix/disconnect.
            <div className="app-detail-buttons">
              <button type="button" className="app-btn app-btn-ghost" onClick={() => handleManageConnections(p.key)}>
                {t("integrations_manage_connection")}
              </button>
            </div>
          ) : (
            <div className="app-detail-implicit">
              <FontAwesomeIcon icon={faCircleCheck} style={{ color: "#5616ea", marginRight: "8px" }} />
              {t("integrations_implicit_note")}
            </div>
          )
        ) : (
          <div className="app-detail-buttons">
            <button type="button" className="app-btn app-btn-primary" onClick={() => handleConnect(p.key)}>
              {count > 0 ? t("integrations_add_another_account_lc") : t("integrations_connect_provider", { provider: p.label })}
            </button>
            {count > 0 && (
              <button type="button" className="app-btn app-btn-ghost" onClick={() => handleManageConnections(p.key)}>
                {t("integrations_manage_connection")}
              </button>
            )}
          </div>
        )}

        {modals}
      </div>
    );
  }

  if (variant === 'cards') {
    return <>{cards}{modals}</>;
  }

  return (
    <div className="data-connect-container">
      <h2>
        {t("integrations_connect_apps_title")}
        <span className="beta-badge">{t("beta_badge")}</span>
      </h2>

      <div className="data-info-box">
        <FontAwesomeIcon className="data-info-icon" icon={faCircleInfo} />
        <div className="data-info-content">
          <p>{t("integrations_info_box")}</p>
        </div>
      </div>

      <div className="data-platforms-grid">{cards}</div>

      {modals}
    </div>
  );
}

export default Integrations;
